export type AiChatFailureKind =
  | "quota_exhausted"
  | "server_unavailable"
  | "network_unavailable"
  | "unauthorized"
  | "invalid_request"
  | "incomplete_response";

export function buildAiChatFailureMessage(kind: AiChatFailureKind) {
  switch (kind) {
    case "quota_exhausted":
      return "I couldn't answer just now because the AI usage limit for this workspace has been used up. Please try again a little later.";
    case "network_unavailable":
      return "I couldn't reach the AI service just now. Please check your connection and try again.";
    case "unauthorized":
      return "I couldn't verify your session for AI chat just now. Please refresh the page and try again.";
    case "invalid_request":
      return "I couldn't process that request as sent. Please rephrase it and try again.";
    case "incomplete_response":
      return "I started generating a reply but couldn't finish it cleanly. Please try again.";
    case "server_unavailable":
    default:
      return "I couldn't answer right now because the AI service is temporarily unavailable on the server. Please try again shortly.";
  }
}

export function classifyAiChatFailure({
  status,
  code,
  message,
}: {
  status?: number | null;
  code?: string | null;
  message?: string | null;
}): AiChatFailureKind {
  const normalizedCode = String(code || "").toLowerCase();
  const normalizedMessage = String(message || "").toLowerCase();

  if (status === 429 || normalizedCode.includes("quota") || normalizedCode.includes("resource_exhausted")) {
    return "quota_exhausted";
  }

  if (status === 401 || status === 403 || normalizedCode.includes("unauthorized")) {
    return "unauthorized";
  }

  if (status === 400) {
    return "invalid_request";
  }

  if (
    normalizedMessage.includes("failed to fetch")
    || normalizedMessage.includes("network")
    || normalizedMessage.includes("request to the edge function")
  ) {
    return "network_unavailable";
  }

  return "server_unavailable";
}
